'use client';

import { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';

export function RegexTester() {
  const [pattern, setPattern] = useState('');
  const [flags, setFlags] = useState('g');
  const [testString, setTestString] = useState('');
  const [matches, setMatches] = useState([]);
  const [error, setError] = useState('');
  const [highlightedText, setHighlightedText] = useState('');

  const commonPatterns = [
    { name: 'E-Mail', pattern: '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}', example: 'test@example.com' },
    { name: 'URL', pattern: 'https?://[\\w\\-\\.]+(:\\d+)?(/[\\w\\-\\./?%&=]*)?', example: 'https://example.com/path' },
    { name: 'IP-Adresse', pattern: '\\b(?:\\d{1,3}\\.){3}\\d{1,3}\\b', example: '192.168.1.1' },
    { name: 'Telefon (DE)', pattern: '\\+?49\\s?\\d{3,4}\\s?\\d{7,8}|0\\d{3,4}\\s?\\d{7,8}', example: '+49 123 45678901' },
    { name: 'Datum (DD.MM.YYYY)', pattern: '\\d{2}\\.\\d{2}\\.\\d{4}', example: '24.12.2023' },
    { name: 'Zeit (HH:MM)', pattern: '([01]?\\d|2[0-3]):[0-5]\\d', example: '14:30' },
    { name: 'Hexadezimal', pattern: '#?[0-9A-Fa-f]{6}', example: '#FF5733' },
    { name: 'Ziffern', pattern: '\\d+', example: '12345' },
    { name: 'Wörter', pattern: '\\b\\w+\\b', example: 'Hello World' },
    { name: 'HTML-Tag', pattern: '<[^>]+>', example: '<div class="test">' },
  ];

  useEffect(() => {
    if (!pattern || !testString) {
      setMatches([]);
      setHighlightedText(testString);
      setError('');
      return;
    }

    try {
      const regex = new RegExp(pattern, flags);
      const foundMatches = [];
      let match;

      if (flags.includes('g')) {
        while ((match = regex.exec(testString)) !== null) {
          foundMatches.push({
            match: match[0],
            index: match.index,
            groups: match.slice(1),
          });
        }
      } else {
        match = regex.exec(testString);
        if (match) {
          foundMatches.push({
            match: match[0],
            index: match.index,
            groups: match.slice(1),
          });
        }
      }

      setMatches(foundMatches);
      setError('');
      highlightMatches(foundMatches);
    } catch (err) {
      setError(err.message);
      setMatches([]);
      setHighlightedText(testString);
    }
  }, [pattern, flags, testString]);

  const highlightMatches = (foundMatches) => {
    if (foundMatches.length === 0) {
      setHighlightedText(testString);
      return;
    }

    let result = '';
    let lastIndex = 0;

    foundMatches.forEach((match, i) => {
      result += testString.slice(lastIndex, match.index);
      result += `<mark class="bg-yellow-300 font-bold">${match.match}</mark>`;
      lastIndex = match.index + match.match.length;
    });

    result += testString.slice(lastIndex);
    setHighlightedText(result);
  };

  const loadPattern = (commonPattern) => {
    setPattern(commonPattern.pattern);
    setTestString(commonPattern.example);
  };

  const toggleFlag = (flag) => {
    if (flags.includes(flag)) {
      setFlags(flags.replace(flag, ''));
    } else {
      setFlags(flags + flag);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <h3 className="font-heading font-bold text-2xl mb-4">
        Regex Tester
      </h3>
      <p className="text-gray-700 mb-6">
        Teste Regular Expressions live mit Highlighting und Match-Details. Unterstützt JavaScript Regex.
      </p>

      {/* Common Patterns */}
      <div className="mb-6">
        <label className="block text-sm font-semibold mb-3">
          Häufige Patterns
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
          {commonPatterns.map((p, index) => (
            <button
              key={index}
              onClick={() => loadPattern(p)}
              className="text-left p-2 bg-gray-100 hover:bg-accent hover:text-white rounded text-sm transition-colors"
            >
              {p.name}
            </button>
          ))}
        </div>
      </div>

      {/* Regex Pattern Input */}
      <div className="mb-4">
        <label className="block text-sm font-semibold mb-2">
          Regular Expression Pattern
        </label>
        <div className="flex gap-2">
          <span className="flex items-center text-2xl text-gray-600">/</span>
          <input
            type="text"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            placeholder="[a-z]+"
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent font-mono text-sm"
          />
          <span className="flex items-center text-2xl text-gray-600">/</span>
          <input
            type="text"
            value={flags}
            onChange={(e) => setFlags(e.target.value)}
            placeholder="gmi"
            className="w-20 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent font-mono text-sm"
          />
        </div>
      </div>

      {/* Flags Checkboxes */}
      <div className="flex gap-4 mb-6 text-sm">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={flags.includes('g')}
            onChange={() => toggleFlag('g')}
            className="w-4 h-4 text-accent rounded focus:ring-accent cursor-pointer"
          />
          <span className="text-gray-700"><code className="bg-gray-100 px-1 rounded">g</code> global</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={flags.includes('i')}
            onChange={() => toggleFlag('i')}
            className="w-4 h-4 text-accent rounded focus:ring-accent cursor-pointer"
          />
          <span className="text-gray-700"><code className="bg-gray-100 px-1 rounded">i</code> case-insensitive</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={flags.includes('m')}
            onChange={() => toggleFlag('m')}
            className="w-4 h-4 text-accent rounded focus:ring-accent cursor-pointer"
          />
          <span className="text-gray-700"><code className="bg-gray-100 px-1 rounded">m</code> multiline</span>
        </label>
      </div>

      {/* Test String Input */}
      <div className="mb-6">
        <label className="block text-sm font-semibold mb-2">
          Test String
        </label>
        <textarea
          value={testString}
          onChange={(e) => setTestString(e.target.value)}
          placeholder="Gib deinen Test-Text hier ein..."
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent font-mono text-sm min-h-[150px]"
        />
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-800">
            <strong>⚠️ Regex Fehler:</strong> {error}
          </p>
        </div>
      )}

      {/* Results */}
      {!error && pattern && testString && (
        <div className="space-y-4">
          {/* Match Count */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-900 font-semibold">
              {matches.length} {matches.length === 1 ? 'Treffer' : 'Treffer'}
            </p>
          </div>

          {/* Highlighted Text */}
          {matches.length > 0 && (
            <div>
              <label className="block text-sm font-semibold mb-2">
                Hervorgehobene Treffer
              </label>
              <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                <div
                  className="font-mono text-sm whitespace-pre-wrap break-words"
                  dangerouslySetInnerHTML={{ __html: highlightedText }}
                />
              </div>
            </div>
          )}

          {/* Match Details */}
          {matches.length > 0 && (
            <div>
              <label className="block text-sm font-semibold mb-2">
                Match Details
              </label>
              <div className="space-y-2">
                {matches.map((match, i) => (
                  <div key={i} className="bg-gray-50 border border-gray-300 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-gray-500">Match {i + 1}</span>
                          <span className="text-xs text-gray-500">@ Position {match.index}</span>
                        </div>
                        <code className="bg-yellow-200 px-2 py-1 rounded font-mono text-sm font-bold">
                          {match.match}
                        </code>
                        {match.groups.length > 0 && (
                          <div className="mt-2">
                            <span className="text-xs font-semibold text-gray-600">Gruppen:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {match.groups.map((group, gi) => (
                                group !== undefined && (
                                  <code key={gi} className="bg-blue-100 px-2 py-1 rounded text-xs">
                                    ${gi + 1}: {group}
                                  </code>
                                )
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {matches.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              Keine Treffer gefunden
            </div>
          )}
        </div>
      )}

      {/* Quick Reference */}
      <div className="mt-8 bg-gray-50 rounded-lg p-6">
        <h4 className="font-semibold text-lg mb-4">Regex Quick Reference</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-semibold mb-2">Zeichen-Klassen</p>
            <ul className="space-y-1 text-gray-700">
              <li><code className="bg-gray-200 px-1 rounded">\d</code> = Ziffer [0-9]</li>
              <li><code className="bg-gray-200 px-1 rounded">\w</code> = Wortzeichen [a-zA-Z0-9_]</li>
              <li><code className="bg-gray-200 px-1 rounded">\s</code> = Leerzeichen</li>
              <li><code className="bg-gray-200 px-1 rounded">.</code> = Beliebiges Zeichen</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold mb-2">Quantifizierer</p>
            <ul className="space-y-1 text-gray-700">
              <li><code className="bg-gray-200 px-1 rounded">*</code> = 0 oder mehr</li>
              <li><code className="bg-gray-200 px-1 rounded">+</code> = 1 oder mehr</li>
              <li><code className="bg-gray-200 px-1 rounded">?</code> = 0 oder 1</li>
              <li><code className="bg-gray-200 px-1 rounded">{"{n,m}"}</code> = n bis m mal</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold mb-2">Anker</p>
            <ul className="space-y-1 text-gray-700">
              <li><code className="bg-gray-200 px-1 rounded">^</code> = Zeilenanfang</li>
              <li><code className="bg-gray-200 px-1 rounded">$</code> = Zeilenende</li>
              <li><code className="bg-gray-200 px-1 rounded">\b</code> = Wortgrenze</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold mb-2">Gruppen</p>
            <ul className="space-y-1 text-gray-700">
              <li><code className="bg-gray-200 px-1 rounded">(abc)</code> = Capture Group</li>
              <li><code className="bg-gray-200 px-1 rounded">(?:abc)</code> = Non-capturing Group</li>
              <li><code className="bg-gray-200 px-1 rounded">[abc]</code> = Zeichenklasse</li>
              <li><code className="bg-gray-200 px-1 rounded">a|b</code> = Oder</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
